import { useRef, useState } from "react";
import MainModal from "components/MainModal";
import { useTranslation } from "react-i18next";
import { useStyles } from "./RCAEditActionPlanModal.styles";
import { Formik, FormikProps, Form } from "formik";
import { useRCAMainStyles } from "../../RCA.styles";
import { editRCAActionPlanRequestFormValidationSchema } from "validations/schemas";
import { Box, Button, InputLabel, TextField, Typography } from "@mui/material";
import { ActionPlanEntity } from "entities/RCA/ActionPlanEntity/ActionPlanEntity";
import DoneIcon from "@mui/icons-material/Done";
import { IRCAEditActionPlanModal, IRCAEditActionPlanModalFormValues } from "./RCAEditActionPlanModalInterfaces";
import { NAME_MAX_INPUT_RCA_ACTION_PLAN } from "constants/constants";

const RCAEditActionPlanModal = ({ open, onClose, actionPlan, onEditActionPlan, onError }: IRCAEditActionPlanModal) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const formRef = useRef<FormikProps<IRCAEditActionPlanModalFormValues>>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { updateActionPlanMutation } = ActionPlanEntity.UpdateActionPlan();

    const initFormValues: IRCAEditActionPlanModalFormValues = {
        actionPlanName: actionPlan.name,
        actionPlanDescription: actionPlan.description,
    };

    const handleSubmit = async (values: IRCAEditActionPlanModalFormValues) => {
        setIsSaving(true);
        const actionPlanValues = {
            description: values.actionPlanDescription,
            name: values.actionPlanName,
        };

        try {
            const response = await updateActionPlanMutation({
                variables: { actionPlanId: actionPlan.actionPlanId, changes: actionPlanValues },
            });

            setIsSaving(false);
            if (response) {
                await onEditActionPlan();
            }
        } catch {
            setIsSaving(false);
            onError?.();
            return;
        }
    };

    const footer = (
        <div className={classes.footer}>
            <Button variant="text" color="info" onClick={onClose}>
                {t("main_modal.cancel_button")}
            </Button>
            <Button
                variant="contained"
                color="info"
                onClick={() => {
                    if (formRef.current) {
                        formRef.current.handleSubmit();
                    }
                }}
            >
                <DoneIcon />
                {t("rca.action_plans.add_action_plan_modal.save_changes")}
            </Button>
        </div>
    );

    return (
        <MainModal
            isOpen={open}
            onClose={onClose}
            title={t("rca.action_plans.action_plan_details.edit_action_plan")}
            showCloseButton
            customModalStyles={{ width: "90%", maxWidth: 1000 }}
            footer={footer}
        >
            <Formik
                innerRef={formRef}
                initialValues={initFormValues}
                validationSchema={editRCAActionPlanRequestFormValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, values, handleChange }) => {
                    return (
                        <Form>
                            <div className={classes.mainContainer}>
                                <div className={classes.contentWrapper}>
                                    <div className={classes.form}>
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-actionPlanDescription-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t("rca.action_plans.add_action_plan_modal.action_plan_name")}
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                disabled={isSaving}
                                                aria-labelledby="outlined-actionPlanDescription-label"
                                                fullWidth
                                                id="outlined-actionPlanDescription-input"
                                                className={mainClasses.contentFormTextField}
                                                name="actionPlanName"
                                                type="text"
                                                variant="outlined"
                                                value={values.actionPlanName}
                                                onChange={handleChange}
                                                error={touched.actionPlanName && Boolean(errors.actionPlanName)}
                                                helperText={
                                                    touched.actionPlanName && errors.actionPlanName
                                                        ? t(errors.actionPlanName)
                                                        : `${values.actionPlanName.length}/${NAME_MAX_INPUT_RCA_ACTION_PLAN}`
                                                }
                                                inputProps={{ maxLength: NAME_MAX_INPUT_RCA_ACTION_PLAN }}
                                                FormHelperTextProps={{
                                                    sx: {
                                                        ...(touched.actionPlanName && errors.actionPlanName
                                                            ? {}
                                                            : { textAlign: "right" }),
                                                        margin: 0,
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-actionPlanDescription-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t(
                                                        "rca.action_plans.add_action_plan_modal.action_plan_description",
                                                    )}
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                disabled={isSaving}
                                                aria-labelledby="outlined-actionPlanDescription-label"
                                                fullWidth
                                                id="outlined-actionPlanDescription-input"
                                                className={mainClasses.contentFormTextField}
                                                name="actionPlanDescription"
                                                type="text"
                                                variant="outlined"
                                                rows={3}
                                                multiline
                                                value={values.actionPlanDescription}
                                                onChange={handleChange}
                                                error={
                                                    touched.actionPlanDescription &&
                                                    Boolean(errors.actionPlanDescription)
                                                }
                                                helperText={
                                                    touched.actionPlanDescription &&
                                                    errors.actionPlanDescription &&
                                                    t(errors.actionPlanDescription)
                                                }
                                            />
                                        </Box>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </MainModal>
    );
};

export default RCAEditActionPlanModal;
